import { useEffect, useRef, useState } from "react";
import api, { apiError } from "../lib/api";
import jsPDF from "jspdf";
import { toast } from "sonner";
import { Download, Receipt, Upload, Send, Trash2, Copy } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function InvoicesPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === "es" ? "es-ES" : "it-IT";
  const [invoices, setInvoices] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [patients, setPatients] = useState({});
  const [patientsArr, setPatientsArr] = useState([]);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadForm, setUploadForm] = useState({ patient_id: "", notes: "" });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const load = async () => {
    const [inv, ups, pts] = await Promise.all([
      api.get("/invoices"),
      api.get("/invoice-uploads"),
      api.get("/patients"),
    ]);
    setInvoices(inv.data);
    setUploads(ups.data);
    setPatientsArr(pts.data);
    const map = {}; pts.data.forEach((p) => (map[p.id] = p)); setPatients(map);
  };
  useEffect(() => { load(); }, []);

  function downloadPDF(inv) {
    const patient = patients[inv.patient_id] || { full_name: inv.patient_name || "Paciente", phone: "", email: "" };
    const doc = new jsPDF();
    doc.setFontSize(22); doc.text("FACTURA", 20, 25);
    doc.setFontSize(10); doc.text(`Nº: ${inv.number}`, 150, 25);
    doc.text(`${t("common.date")}: ${new Date(inv.created_at).toLocaleDateString(locale)}`, 150, 32);
    doc.setFontSize(11);
    doc.text("Cliente:", 20, 50);
    doc.text(patient.full_name, 20, 57);
    if (patient.email) doc.text(patient.email, 20, 63);
    let y = 90;
    doc.setFontSize(9);
    doc.text("CONCEPTO", 20, y); doc.text("CANT.", 130, y); doc.text("PRECIO", 150, y); doc.text("TOTAL", 180, y);
    doc.line(20, y + 2, 195, y + 2); y += 10;
    inv.items.forEach((it) => {
      doc.text(it.description.substring(0, 60), 20, y);
      doc.text(String(it.quantity), 130, y);
      doc.text(`€${it.unit_price.toFixed(2)}`, 150, y);
      doc.text(`€${(it.quantity * it.unit_price).toFixed(2)}`, 180, y);
      y += 8;
    });
    y += 4; doc.line(130, y, 195, y); y += 6;
    doc.text(`Subtotal:`, 150, y); doc.text(`€${inv.subtotal.toFixed(2)}`, 180, y); y += 6;
    doc.text(`IVA (${inv.tax_percent}%):`, 150, y); doc.text(`€${inv.tax.toFixed(2)}`, 180, y); y += 6;
    doc.setFontSize(12); doc.text(`TOTAL:`, 150, y); doc.text(`€${inv.total.toFixed(2)}`, 180, y);
    doc.save(`factura-${inv.number}.pdf`);
  }

  const total = invoices.reduce((s, i) => s + i.total, 0);

  async function onFile(e) {
    const file = e.target.files?.[0]; if (!file) return;
    if (!uploadForm.patient_id) { toast.error(t("invoices.patientRequired")); e.target.value = ""; return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/invoice-uploads?patient_id=${uploadForm.patient_id}&notes=${encodeURIComponent(uploadForm.notes)}`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("invoices.uploaded")); setShowUpload(false); setUploadForm({ patient_id: "", notes: "" }); load();
    } catch (e) { toast.error(apiError(e)); } finally { setUploading(false); e.target.value = ""; }
  }

  function shareInvoice(u) {
    const link = `${process.env.REACT_APP_BACKEND_URL}/api/public/invoice/${u.share_token}`;
    const phone = (u.patient_phone || "").replace(/[\s-]/g, "").replace("+", "");
    const msg = `Ciao ${u.patient_name}, aquí tiene su factura: ${link}`;
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, "_blank");
  }
  function copyInvoiceLink(u) {
    navigator.clipboard.writeText(`${process.env.REACT_APP_BACKEND_URL}/api/public/invoice/${u.share_token}`);
    toast.success(t("common.linkCopied"));
  }
  async function delUpload(id) {
    if (!window.confirm(t("invoices.confirmDelete"))) return;
    await api.delete(`/invoice-uploads/${id}`); load();
  }

  return (
    <div className="space-y-8 fade-in-up" data-testid="invoices-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-stone-500 mb-3">{t("invoices.eyebrow")}</p>
          <h1 className="font-heading text-5xl font-light">{t("invoices.title")}</h1>
        </div>
        <div className="flex flex-wrap gap-3">
          <button onClick={() => setShowUpload(!showUpload)} data-testid="btn-upload-invoice"
            className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#D4A373] text-stone-900 hover:bg-[#B07D4F] hover:text-white transition-colors font-medium">
            <Upload className="w-4 h-4" /> {t("invoices.uploadPdf")}
          </button>
        </div>
      </div>

      {showUpload && (
        <div className="bg-white border border-stone-200 rounded-2xl p-6 md:p-8 space-y-4" data-testid="upload-invoice-form">
          <h3 className="font-heading text-2xl font-light">{t("invoices.uploadTitle")}</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{t("invoices.colPatient")} *</label>
              <select value={uploadForm.patient_id} onChange={(e) => setUploadForm({ ...uploadForm, patient_id: e.target.value })} data-testid="upload-patient"
                className="w-full px-4 py-2.5 bg-[#FAF9F6] border border-stone-200 rounded-xl">
                <option value="">{t("invoices.selectPatient")}</option>
                {patientsArr.map((p) => <option key={p.id} value={p.id}>{p.full_name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{t("common.notes")}</label>
              <input value={uploadForm.notes} onChange={(e) => setUploadForm({ ...uploadForm, notes: e.target.value })}
                className="w-full px-4 py-2.5 bg-[#FAF9F6] border border-stone-200 rounded-xl" />
            </div>
          </div>
          <input ref={fileRef} type="file" accept="application/pdf" onChange={onFile} className="hidden" />
          <button onClick={() => fileRef.current.click()} disabled={uploading} data-testid="upload-invoice-file"
            className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-stone-900 text-white hover:bg-stone-800">
            <Upload className="w-4 h-4" /> {uploading ? t("patientDetail.uploading") : t("invoices.selectPdf")}
          </button>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-6">
        <div className="bg-white border border-stone-200 rounded-2xl p-6">
          <p className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2">{t("invoices.issued")}</p>
          <div className="font-heading text-4xl font-light">{invoices.length}</div>
        </div>
        <div className="bg-white border border-stone-200 rounded-2xl p-6">
          <p className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2">{t("invoices.totalBilled")}</p>
          <div className="font-heading text-4xl font-light">€{total.toFixed(2)}</div>
        </div>
        <div className="bg-[#1C1917] text-white rounded-2xl p-6">
          <p className="text-xs uppercase tracking-[0.2em] text-stone-400 mb-2">{t("invoices.avgInvoice")}</p>
          <div className="font-heading text-4xl font-light">€{invoices.length ? (total / invoices.length).toFixed(2) : "0.00"}</div>
        </div>
      </div>

      <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
        {uploads.length === 0 && invoices.length === 0 ? (
          <div className="py-24 text-center text-stone-500">
            <Receipt className="w-8 h-8 mx-auto mb-3 text-stone-300" strokeWidth={1} />
            <p className="text-sm">{t("invoices.empty")}</p>
          </div>
        ) : (
          <>
            {uploads.length > 0 && (
              <div>
                <div className="px-6 py-3 border-b border-stone-100 text-xs uppercase tracking-[0.2em] text-stone-500">{t("invoices.uploadedInvoices")}</div>
                <table className="w-full">
                  <thead>
                    <tr className="text-xs uppercase tracking-[0.15em] text-stone-500 border-b border-stone-100">
                      <th className="text-left px-6 py-3">{t("invoices.colPatient")}</th>
                      <th className="text-left px-6 py-3">{t("invoices.colFile")}</th>
                      <th className="text-left px-6 py-3 hidden md:table-cell">{t("invoices.colDate")}</th>
                      <th className="px-6 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {uploads.map((u) => (
                      <tr key={u.id} data-testid={`upload-row-${u.id}`} className="border-b border-stone-50 hover:bg-stone-50">
                        <td className="px-6 py-4 text-sm">{u.patient_name || "—"}</td>
                        <td className="px-6 py-4 text-sm text-stone-600 truncate max-w-[280px]">{u.original_filename}</td>
                        <td className="px-6 py-4 text-sm text-stone-500 hidden md:table-cell">{new Date(u.created_at).toLocaleDateString(locale)}</td>
                        <td className="px-6 py-4 text-right space-x-3 whitespace-nowrap">
                          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/public/invoice/${u.share_token}`} target="_blank" rel="noreferrer"
                            data-testid={`view-invoice-${u.id}`} className="text-xs uppercase tracking-[0.2em] text-[#D4A373] hover:text-[#B07D4F]">
                            <Download className="w-3.5 h-3.5 inline mr-1" /> {t("invoices.view")}
                          </a>
                          <button onClick={() => shareInvoice(u)} data-testid={`send-invoice-${u.id}`} className="text-xs uppercase tracking-[0.2em] text-[#8FA08B] hover:opacity-80">
                            <Send className="w-3.5 h-3.5 inline mr-1" /> WhatsApp
                          </button>
                          <button onClick={() => copyInvoiceLink(u)} className="text-stone-400 hover:text-stone-900"><Copy className="w-3.5 h-3.5" /></button>
                          <button onClick={() => delUpload(u.id)} className="text-stone-400 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {invoices.length > 0 && (
              <div>
                <div className="px-6 py-3 border-b border-stone-100 text-xs uppercase tracking-[0.2em] text-stone-500">{t("invoices.generated")}</div>
                <table className="w-full">
                  <thead>
                    <tr className="text-xs uppercase tracking-[0.15em] text-stone-500 border-b border-stone-100">
                      <th className="text-left px-6 py-3">{t("invoices.colNumber")}</th>
                      <th className="text-left px-6 py-3">{t("invoices.colPatient")}</th>
                      <th className="text-left px-6 py-3">{t("invoices.colDate")}</th>
                      <th className="text-right px-6 py-3">{t("invoices.colTotal")}</th>
                      <th className="px-6 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((inv) => (
                      <tr key={inv.id} data-testid={`all-invoice-${inv.id}`} className="border-b border-stone-50 hover:bg-stone-50">
                        <td className="px-6 py-4 font-medium">{inv.number}</td>
                        <td className="px-6 py-4 text-sm">{inv.patient_name || patients[inv.patient_id]?.full_name || "—"}</td>
                        <td className="px-6 py-4 text-sm text-stone-600">{new Date(inv.created_at).toLocaleDateString(locale)}</td>
                        <td className="px-6 py-4 text-right font-medium">€{inv.total.toFixed(2)}</td>
                        <td className="px-6 py-4 text-right">
                          <button onClick={() => downloadPDF(inv)} data-testid={`download-inv-${inv.id}`}
                            className="text-xs uppercase tracking-[0.2em] text-[#D4A373] hover:text-[#B07D4F]">
                            <Download className="w-3.5 h-3.5 inline mr-1" /> PDF
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
